// DEPENDENCIES
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";

// CSS
import "./PhaseConsoleEditAddDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import {
	postAPICall,
	putAPICall,
	getAPICall,
} from "../../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	ButtonGroup,
	CircularProgress,
	debounce,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormHelperText,
	FormLabel,
	IconButton,
	TextField,
} from "@mui/material";

// MUI ICONS
import CloseIcon from "@mui/icons-material/Close";

// CUSTOM COMPONENTS
import DropboxFileSelector from "../../../Misc/DropboxFileSelector/DropboxFileSelector";
import {
	convertToPST,
	convertToPSTYearOnly,
} from "../../../../constants/dateTime";

// CONSTANTS
import { generateAlphanumericCode } from "../../../../constants/constants";

// OTHER

// IDs of the OIC custom field's dropdown values in BQE — same hardcoded mapping
// PropertyConsoleEditAddDialog/PendingJobReviewScreen use when associating a
// newly-created BQE record's OIC.
const BQE_OIC_CUSTOM_FIELD_VALUES = {
	WA: "d5e226fe-5798-43a4-a752-7bcd087eeda3",
	OR: "03356b2a-8467-46ef-9ce7-cf4dc99599d3",
	UT: "60d08179-1541-41ee-938f-9415ba4b5872",
};

// Adds a "<code>.1 Videos" sibling under every "<code> Photos" folder in the given
// list — mirrors PendingJobReviewScreen's withVideosUnderPhotos convention.
const withVideosUnderPhotos = (paths) => {
	const results = [...paths];
	paths.forEach((p) => {
		const m = p.match(/\/(\d+\.\d+)\s+Photos$/);
		if (!m) return;
		results.push(`${p}/${m[1]}.1 Videos`);
	});
	return results;
};

// The "still looks selected" blue disabled style should only show when a toggle
// button is disabled because it's already the active mode. When it's disabled
// because a prerequisite hasn't been picked yet, let MUI's default grayed-out
// disabled style show instead — otherwise every disabled button looks "selected".
const activeModeButtonSx = (isDisabledBecauseActive) =>
	isDisabledBecauseActive
		? { "&.Mui-disabled": { background: "#184C93", color: "#ffffff" } }
		: undefined;

// REACT COMPONENT
const PhaseConsoleEditAddDialog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// REFS
	// Guards fetchBqeParentProjectRecord against out-of-order responses — if the
	// selected project changes again before an in-flight request resolves, the
	// stale response is discarded instead of overwriting the newer selection's data.
	const bqeParentProjectRequestRef = useRef(0);

	// STATES
	const [loading, setLoading] = useState(true);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	// General inputs
	const [phaseName, setPhaseName] = useState(null);
	const [phaseProperty, setPhaseProperty] = useState(null);
	const [phaseProject, setPhaseProject] = useState(null);
	const [phaseDropBoxFolder, setPhaseDropBoxFolder] = useState("");
	const [companyCamID, setCompanyCamID] = useState("");

	const [bqePhases, setBqePhases] = useState([]);
	const [selectedBqePhase, setSelectedBqePhase] = useState(null);

	// "existing" reuses a BQE phase found via search (original behavior); "create"
	// creates a brand-new BQE phase (via /api/bqe/phase/create) nested under the
	// selected project's own BQE record, inheriting its client/manager/class — there's
	// no "none" mode, a Tools phase always needs a BQE phase behind it.
	const [bqePhaseMode, setBqePhaseMode] = useState("existing");
	const [bqeParentProjectRecord, setBqeParentProjectRecord] = useState(null);
	const [bqeParentProjectLoading, setBqeParentProjectLoading] = useState(false);

	// "existing" browses/selects an existing Dropbox folder (original behavior); "create"
	// creates a brand-new folder, under the selected project's own Dropbox folder,
	// named "<steve_number>. <phase abbreviation>" — matching PendingJobReviewScreen's
	// phase-folder convention — plus its phase-type-specific subfolders. No "none" mode
	// and no free-text naming — a Tools phase always needs a folder, auto-named.
	const [dropboxMode, setDropboxMode] = useState("existing");

	// "existing" searches CompanyCam by name and reuses a chosen project's ID (replaces
	// the original bare free-text ID field); "create" creates a brand-new CompanyCam
	// project (via /api/companyCam/projects) named after this phase — no "none" mode,
	// a Tools phase always needs a CompanyCam project behind it.
	const [companyCamMode, setCompanyCamMode] = useState("existing");
	const [companyCamOptions, setCompanyCamOptions] = useState([]);
	const [companyCamSearchLoading, setCompanyCamSearchLoading] = useState(false);
	const [companyCamSearchValue, setCompanyCamSearchValue] = useState("");
	const [companyCamRecord, setCompanyCamRecord] = useState(null);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// The BQE Phase / Dropbox Folder / CompanyCam Project "Create New" options all
	// derive their name (and, for BQE, their parent record) from the Tools project
	// and phase name selections — so none of them are usable until both are picked.
	const phaseSelectionsComplete = Boolean(phaseProject) && Boolean(phaseName);

	// USE EFFECT
	useEffect(() => {
		// populate fields if editing, or if adding with a prefill (the Project Viewer's
		// "Link Phase to Tools" — an existing BQE phase that has no Tools record yet)
		if (props.editing) {
			handlePopulateFieldsOnEdit(props.recordToEdit);
		} else if (props.open && props.prefillRecord) {
			handlePopulateFieldsOnEdit(props.prefillRecord);
		} else {
			setLoading(false);
		}
	}, [props.open, props.recordToEdit, props.prefillRecord]);

	// If phase project is selected, fetch the BQE phases that are associated with the project
	useEffect(() => {
		if (phaseProject?.id) {
			fetchBQEPhases();
		}
	}, [phaseProject]);

	// Refetch the parent project's raw BQE record whenever "Create New" BQE phase
	// mode is active and the selected project changes, so the inherited
	// client/manager/class/address always match whichever project is selected.
	useEffect(() => {
		if (bqePhaseMode !== "create") return;
		fetchBqeParentProjectRecord();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bqePhaseMode, phaseProject]);

	// If the project/phase-name selections are cleared after a "Create New" mode was
	// already chosen, drop back to "existing" — the buttons that reach "create" are
	// disabled until both are selected, but this also covers un-selecting afterward.
	useEffect(() => {
		if (phaseSelectionsComplete) return;
		setBqePhaseMode((mode) => (mode === "create" ? "existing" : mode));
		setDropboxMode((mode) => (mode === "create" ? "existing" : mode));
		setCompanyCamMode((mode) => (mode === "create" ? "existing" : mode));
	}, [phaseSelectionsComplete]);

	// INPUT HANDLERS

	// general inputs

	const handlePhasePropertyChange = (event, newValue) => {
		setPhaseProperty(newValue);
	};

	const handlePhaseProjectChange = (event, newValue) => {
		setPhaseProject(newValue);

		if (newValue) {
			setPhaseDropBoxFolder(newValue.dropbox_path);
		}
	};

	// const handlePhaseTypeChange = (event, newValue) => {
	// 	setPhaseType(newValue);
	// };

	const handlePhaseDropBoxFolderChange = (event) => {
		setPhaseDropBoxFolder(event.target.value);
	};

	const handlePhaseNameChange = (event, newValue) => {
		setPhaseName(newValue);
	};

	// BQE PHASE MODE HANDLERS

	const bqePhaseExistingClicked = () => {
		setBqePhaseMode("existing");
		setBqeParentProjectRecord(null);
	};

	const bqePhaseCreateClicked = () => {
		setBqePhaseMode("create");
		setSelectedBqePhase(null);
	};

	// DROPBOX MODE HANDLERS

	const dropboxExistingClicked = () => setDropboxMode("existing");

	const dropboxCreateClicked = () => setDropboxMode("create");

	// COMPANYCAM MODE HANDLERS

	const companyCamExistingClicked = () => {
		setCompanyCamMode("existing");
		setCompanyCamID("");
		setCompanyCamRecord(null);
		setCompanyCamSearchValue("");
		setCompanyCamOptions([]);
	};

	const companyCamCreateClicked = () => {
		setCompanyCamMode("create");
		setCompanyCamID("");
		setCompanyCamRecord(null);
		setCompanyCamSearchValue("");
		setCompanyCamOptions([]);
	};

	const companyCamProjectSelected = (event, newValue) => {
		if (newValue && newValue.id) {
			setCompanyCamRecord(newValue);
			setCompanyCamID(newValue.id);
			setCompanyCamSearchValue(newValue.name || newValue.id);
		} else {
			setCompanyCamRecord(null);
			setCompanyCamID("");
			setCompanyCamSearchValue("");
			setCompanyCamOptions([]);
		}
	};

	const companyCamSearchByNameChanged = useMemo(
		() =>
			debounce(async (event) => {
				setCompanyCamSearchLoading(true);
				const textLength = event.target.value.length;

				// if the current text length is less than 3, we will get too many results, so return
				if (textLength < 3) {
					setCompanyCamSearchLoading(false);
					setCompanyCamOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/companyCam/projects",
						{ query: event.target.value }
					);
					setCompanyCamOptions(Array.isArray(res.data) ? res.data : []);
				} catch (error) {
					console.log("Error searching CompanyCam projects: ", error);
					setCompanyCamOptions([]);
				}
				setCompanyCamSearchLoading(false);
			}, 500),
		[]
	);

	// handles populating fields when editing (or prefilling an add)
	const handlePopulateFieldsOnEdit = async (record) => {
		setLoading(true);
		// editing always searches for existing BQE/Dropbox/CompanyCam records — the
		// Tools record is already linked to real ones, so there's nothing to create
		setBqePhaseMode("existing");
		setBqeParentProjectRecord(null);
		setDropboxMode("existing");
		setCompanyCamMode("existing");
		setCompanyCamRecord(null);
		setCompanyCamSearchValue("");
		setCompanyCamOptions([]);
		setPhaseProperty(
			props.properties.find(
				(property) => property.id === record.property_id
			) || null
		);
		setPhaseProject(
			props.projects.find(
				(project) => project.id === record.project_id
			) || null
		);

		setPhaseName(
			props.phaseNames.find(
				(phaseName) => phaseName.name === record.name
			) || null
		);

		await fetchBQEPhase(record.bqe_id);

		setPhaseDropBoxFolder(record.dropbox_path || "");
		setCompanyCamID(record.company_cam_id || "");
		setLoading(false);
	};

	// API FUNCTIONS
	const handleAddRecord = async () => {
		setLoading(true);

		try {
			const bqePhaseForSave =
				bqePhaseMode === "create"
					? await createBqePhaseRecord()
					: selectedBqePhase;

			const resolvedDropboxPath = await resolveDropboxPath();

			const companyCamIDForSave =
				companyCamMode === "create"
					? (await createCompanyCamRecord()).id
					: companyCamID;

			await postAPICall(
				instance,
				accounts[0],
				"/api/jobs/phase/create",
				{
					name: phaseName.name,
					dropboxPath: resolvedDropboxPath,
					bqeID: bqePhaseForSave.id,
					bqeCode: bqePhaseForSave.code,
					hubspotID: phaseProject.hubspot_id,
					companyCamID: companyCamIDForSave,
					propertyID: phaseProperty.id,
					projectID: phaseProject.id,
					ownerID: phaseProject.owner_id,
					phaseTypeID: phaseName.phase_type_id,
					modifiedBy: employeeID,
				}
			);
			setLoading(false);
			return true;
		} catch (error) {
			console.log(error);
			setAlertMessage(error?.message || "Error adding record");
			setAlertOpen(true);
			setLoading(false);
			return false;
		}
	};

	const handleUpdateRecord = async () => {
		setLoading(true);
		try {
			const bqePhaseForSave =
				bqePhaseMode === "create"
					? await createBqePhaseRecord()
					: selectedBqePhase;

			const resolvedDropboxPath = await resolveDropboxPath();

			const companyCamIDForSave =
				companyCamMode === "create"
					? (await createCompanyCamRecord()).id
					: companyCamID;

			await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/phase/update",
				{
					id: props.recordToEdit.id,
					name: phaseName.name,
					dropboxPath: resolvedDropboxPath,
					bqeID: bqePhaseForSave.id,
					bqeCode: bqePhaseForSave.code,
					hubspotID: phaseProject.hubspot_id,
					companyCamID: companyCamIDForSave,
					propertyID: phaseProperty.id,
					projectID: phaseProject.id,
					ownerID: phaseProject.owner_id,
					phaseTypeID: phaseName.phase_type_id,
					modifiedBy: employeeID,
				}
			);
			setLoading(false);
			return true;
		} catch (error) {
			console.log(error);
			setAlertMessage(error?.message || "Error updating record");
			setAlertOpen(true);
			setLoading(false);
			return false;
		}
	};

	const fetchBQEPhases = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/phases/byProject",
				{
					projectID: phaseProject.bqe_id,
				}
			);

			// BQE returns an empty string (not an empty array) when there are no
			// matches — e.g. a freshly-created project with no phases yet.
			setBqePhases(Array.isArray(res.data) ? res.data : []);
		} catch (error) {
			console.log(error);
			setAlertMessage("Error fetching BQE phases");
			setAlertOpen(true);
		}
	};

	const fetchBQEPhase = async (bqeID) => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/byID",
				{
					id: bqeID,
				}
			);

			if (res.data) {
				setSelectedBqePhase(res.data);
			}
		} catch (error) {
			console.log(error);
			setAlertMessage("Error fetching BQE phase");
			setAlertOpen(true);
		}
	};

	// fetches the raw BQE record for the selected project, used only in "Create New"
	// BQE phase mode to inherit its client/manager/class/address
	const fetchBqeParentProjectRecord = async () => {
		const requestId = ++bqeParentProjectRequestRef.current;

		if (!phaseProject?.bqe_id) {
			setBqeParentProjectRecord(null);
			return;
		}
		setBqeParentProjectLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/byID",
				{ id: phaseProject.bqe_id }
			);
			if (requestId !== bqeParentProjectRequestRef.current) return;
			setBqeParentProjectRecord(res.data || null);
		} catch (error) {
			console.log("Error fetching parent BQE project record: ", error);
			if (requestId === bqeParentProjectRequestRef.current) {
				setBqeParentProjectRecord(null);
			}
		}
		if (requestId === bqeParentProjectRequestRef.current) {
			setBqeParentProjectLoading(false);
		}
	};

	// generates a unique BQE phase code, retrying up to 10 times against the local
	// tools DB — mirrors PendingJobReviewScreen's generatePhaseCode
	const generateUniqueBqePhaseCode = async () => {
		let phaseCode = `PH-${generateAlphanumericCode()}`;

		for (let attempt = 0; attempt < 10; attempt++) {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/phase/byBQECode",
				{ bqeCode: phaseCode }
			);

			if (res.data.length === 0) return phaseCode;

			phaseCode = `PH-${generateAlphanumericCode()}`;
		}

		throw new Error("Failed to generate a unique BQE phase code");
	};

	// best-effort — a failure here shouldn't undo the phase record that was
	// already successfully created
	const associateBqeOicCustomField = async (bqeProjectId, oic) => {
		try {
			const bqeOICValue = BQE_OIC_CUSTOM_FIELD_VALUES[oic] || BQE_OIC_CUSTOM_FIELD_VALUES.WA;
			await postAPICall(instance, accounts[0], "/api/bqe/oic/assign", {
				bqeProjectId,
				bqeOICValue,
			});
		} catch (error) {
			console.log("Error associating BQE OIC custom field: ", error);
		}
	};

	// creates a brand-new BQE phase nested under the selected project's own BQE
	// record, inheriting its client/manager/class instead of asking for them again
	const createBqePhaseRecord = async () => {
		if (!bqeParentProjectRecord) {
			throw new Error("Missing the project's BQE record — select a project with a linked BQE record first");
		}

		const phaseCode = await generateUniqueBqePhaseCode();
		const address = bqeParentProjectRecord.address?.[0] || {};

		const res = await postAPICall(
			instance,
			accounts[0],
			"/api/bqe/phase/create",
			{
				clientId: bqeParentProjectRecord.clientId,
				phaseCode,
				managerId: bqeParentProjectRecord.managerId,
				contractType: 0,
				phaseName: phaseName.name,
				addressLine1: address.street1,
				addressLine2: address.street2,
				city: address.city,
				state: address.state,
				zip: address.zip,
				oic: phaseProperty?.oic,
				classId: bqeParentProjectRecord.classId,
				parentId: bqeParentProjectRecord.id,
				rootId: bqeParentProjectRecord.rootProjectId,
				contractAmount: 0,
			}
		);
		if (!res.data || !res.data.id) {
			throw new Error("Failed to create BQE phase record");
		}

		await associateBqeOicCustomField(res.data.id, phaseProperty?.oic);

		return res.data;
	};

	// mirrors PendingJobReviewScreen's CompanyCam project naming convention:
	// "<property>: <project>: <phase>"
	const buildCompanyCamProjectName = () =>
		[
			phaseProperty?.name,
			phaseProject?.display_name || phaseProject?.name,
			phaseName?.abbreviated_name || phaseName?.name,
		]
			.filter(Boolean)
			.join(": ");

	// creates a brand-new CompanyCam project named after this phase. Reuses the BQE
	// parent project's address when it's already been fetched (i.e. BQE phase mode is
	// also "create") — address is otherwise omitted, since CompanyCam only requires
	// a name to create a project.
	const createCompanyCamRecord = async () => {
		const address = bqeParentProjectRecord?.address?.[0];
		const payload = { name: buildCompanyCamProjectName() };
		if (address) {
			payload.address = {
				street_address_1: address.street1,
				street_address_2: address.street2,
				city: address.city,
				state: address.state,
				postal_code: address.zip,
			};
		}

		const res = await postAPICall(
			instance,
			accounts[0],
			"/api/companyCam/projects",
			payload
		);
		if (!res.data || !res.data.id) {
			throw new Error("Failed to create CompanyCam project");
		}
		return res.data;
	};

	// mirrors PendingJobReviewScreen's phase-folder naming convention:
	// "<steve_number>. <abbreviated phase name>"
	const buildPhaseFolderName = () =>
		`${phaseName?.steve_number}. ${phaseName?.abbreviated_name}`;

	// resolves the dropbox path to save for this phase: the browsed-to existing
	// folder, or a brand-new folder — auto-named, under the selected project's own
	// Dropbox folder — with its phase-type-specific subfolders
	const resolveDropboxPath = async () => {
		if (dropboxMode === "existing") return phaseDropBoxFolder || null;

		const parent = phaseProject?.dropbox_path || "";
		const newPath = `${parent}/${buildPhaseFolderName()}`.replace(/\/{2,}/g, "/");

		const createRes = await postAPICall(
			instance,
			accounts[0],
			"/api/dropbox/create/folder",
			{ path: newPath }
		);
		if (!createRes.data || createRes.data.error) {
			throw new Error("Failed to create Dropbox folder");
		}

		if (phaseName?.phase_type_id) {
			const folderListRes = await getAPICall(
				instance,
				accounts[0],
				"/api/dropbox/folders/byPhaseType",
				{ phaseTypeID: phaseName.phase_type_id }
			);
			const subfolderPaths = withVideosUnderPhotos(
				(folderListRes.data || []).map(
					(folder) => `${newPath}/${folder.folder_name}`
				)
			);
			if (subfolderPaths.length > 0) {
				const batchRes = await postAPICall(
					instance,
					accounts[0],
					"/api/dropbox/create/folder/batch",
					{ paths: subfolderPaths }
				);
				if (batchRes.data && batchRes.data.error) {
					throw new Error(
						"Phase folder created, but failed to create its subfolders"
					);
				}
			}
		}

		return newPath;
	};

	// HELPER FUNCTIONS

	const handleSubmitClick = async () => {
		if (!validateInputFields()) return;
		setLoading(true);
		// if editing, update record, else add record
		const succeeded = props.editing
			? await handleUpdateRecord()
			: await handleAddRecord();

		// Leave the dialog open with the error alert visible on failure —
		// closing here would wipe the alert before the user ever saw it.
		if (!succeeded) return;

		resetInputFields();
		setAlertMessage("");
		setAlertOpen(false);
		setLoading(false);
		props.onClose();
	};

	const handleCancelClick = () => {
		resetInputFields();
		setAlertMessage("");
		setAlertOpen(false);
		props.onCloseNoRefresh();
	};

	const resetInputFields = () => {
		setPhaseName(null);
		setPhaseProperty(null);
		setPhaseProject(null);
		setPhaseDropBoxFolder("");
		setCompanyCamID("");

		setSelectedBqePhase(null);

		setBqePhaseMode("existing");
		setBqeParentProjectRecord(null);
		setBqeParentProjectLoading(false);

		setDropboxMode("existing");

		setCompanyCamMode("existing");
		setCompanyCamOptions([]);
		setCompanyCamSearchLoading(false);
		setCompanyCamSearchValue("");
		setCompanyCamRecord(null);

		setLoading(false);
		setAlertOpen(false);
		setAlertMessage("");
	};

	const validateInputFields = () => {
		if (!phaseProperty || !phaseProject || !phaseName) {
			setAlertMessage("Please fill out all required fields");
			setAlertOpen(true);
			return false;
		}
		if (bqePhaseMode === "existing" && !selectedBqePhase?.id) {
			setAlertMessage("Please select a BQE phase");
			setAlertOpen(true);
			return false;
		}
		if (bqePhaseMode === "create" && !bqeParentProjectRecord) {
			setAlertMessage(
				"The selected project doesn't have a usable BQE record to create a new BQE phase under"
			);
			setAlertOpen(true);
			return false;
		}
		if (dropboxMode === "existing" && !phaseDropBoxFolder) {
			setAlertMessage("Please enter a Dropbox path");
			setAlertOpen(true);
			return false;
		}
		if (dropboxMode === "create" && !phaseProject?.dropbox_path) {
			setAlertMessage(
				"The selected project doesn't have a Dropbox folder to create a phase folder under"
			);
			setAlertOpen(true);
			return false;
		}
		if (companyCamMode === "existing" && !companyCamID) {
			setAlertMessage("Please select a CompanyCam project");
			setAlertOpen(true);
			return false;
		}
		return true;
	};

	// RENDER
	return (
		<Dialog
			id="PhaseConsoleEditAddDialog"
			open={props.open}
			maxWidth="xl"
			fullWidth
			onClose={handleCancelClick}
		>
			<DialogTitle id="PhaseConsoleEditAddDialogTitle">
				{/* edit / add */}
				{props.editing
					? "Edit Phase Record"
					: props.prefillRecord
					? "Link Phase to Tools"
					: "Add Phase Record"}
			</DialogTitle>
			{loading && (
				<div className="phaseConsoleLoadingSpinner">
					<CircularProgress />
				</div>
			)}
			{!loading && (
				<DialogContent id="PhaseConsoleEditAddDialogContent">
					{alertOpen && (
						<Alert
							severity="warning"
							action={
								<IconButton
									aria-label="close"
									color="inherit"
									size="small"
									onClick={() => {
										setAlertOpen(false);
									}}
								>
									<CloseIcon />
								</IconButton>
							}
						>
							{alertMessage}
						</Alert>
					)}

					<div className="dialogSubtitle">Tools Property Record</div>

					<FormControl sx={{ flex: 1 }}>
						<div className="FormBlock">
							<div className="FormRow">
								<Autocomplete
									sx={{ flexGrow: 1 }}
									id="property"
									options={props.properties}
									getOptionLabel={(option) =>
										option ? option.name : ""
									}
									value={phaseProperty}
									onChange={handlePhasePropertyChange}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Property"
											variant="outlined"
										/>
									)}
									renderOption={(props, option) => (
										<li {...props} key={option.id}>
											{option.name}
										</li>
									)}
								/>
							</div>
						</div>
					</FormControl>

					<Divider />

					<div className="dialogSubtitle">Tools Project Record</div>

					<FormControl sx={{ flex: 1 }}>
						<div className="FormBlock">
							<div className="FormRow">
								<Autocomplete
									sx={{ flexGrow: 1 }}
									id="project"
									options={props.projects.filter(
										(project) =>
											project.property_id ===
											phaseProperty?.id
									)}
									getOptionLabel={(option) =>
										option ? option.name : ""
									}
									value={phaseProject}
									onChange={handlePhaseProjectChange}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Project"
											variant="outlined"
										/>
									)}
									renderOption={(props, option) => (
										<li {...props} key={option.id}>
											{option.name}
											<span style={{ margin: "0 6px" }}>
												-
											</span>
											<span style={{ color: "gray" }}>
												{convertToPSTYearOnly(
													option.created
												)}
											</span>
										</li>
									)}
									disabled={!phaseProperty}
								/>
							</div>
						</div>
					</FormControl>

					<Divider />

					<div className="dialogSubtitle">Phase Name</div>

					<FormControl sx={{ flex: 1 }}>
						<div className="FormBlock">
							{/* <div className="FormRow">
								<Autocomplete
									sx={{ flexGrow: 1 }}
									id="phaseType"
									options={props.phaseTypes}
									getOptionLabel={(option) =>
										option ? option.name : ""
									}
									value={phaseType}
									onChange={handlePhaseTypeChange}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Phase Type"
											variant="outlined"
										/>
									)}
									renderOption={(props, option) => (
										<li {...props} key={option.id}>
											{option.name}
										</li>
									)}
								/>
							</div> */}
							<div className="FormRow">
								<Autocomplete
									sx={{ flexGrow: 1 }}
									id="phaseName"
									options={props.phaseNames}
									getOptionLabel={(option) =>
										option ? option.name : ""
									}
									value={phaseName}
									onChange={handlePhaseNameChange}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Phase Name"
											variant="outlined"
										/>
									)}
									disabled={!phaseProject}
								/>
							</div>
						</div>
					</FormControl>

					<Divider />

					<div className="dialogSubtitleWithActions">
						<span>BQE Phase</span>
						{!props.editing && (
							<ButtonGroup size="small">
								<Button
									sx={activeModeButtonSx(
										phaseSelectionsComplete && bqePhaseMode === "existing"
									)}
									variant={bqePhaseMode === "existing" ? "contained" : "outlined"}
									onClick={bqePhaseExistingClicked}
									disabled={bqePhaseMode === "existing" || !phaseSelectionsComplete}
								>
									Search Existing
								</Button>
								<Button
									sx={activeModeButtonSx(
										phaseSelectionsComplete && bqePhaseMode === "create"
									)}
									variant={bqePhaseMode === "create" ? "contained" : "outlined"}
									onClick={bqePhaseCreateClicked}
									disabled={bqePhaseMode === "create" || !phaseSelectionsComplete}
								>
									Create New
								</Button>
							</ButtonGroup>
						)}
					</div>

					{bqePhaseMode === "existing" && phaseSelectionsComplete && (
						<FormControl sx={{ flex: 1 }}>
							<div className="FormBlock">
								<div className="FormRow">
									<Autocomplete
										sx={{ flexGrow: 1 }}
										id="bqePhase"
										options={bqePhases}
										getOptionLabel={(option) =>
											option?.displayName ?? ""
										}
										value={selectedBqePhase}
										onChange={(event, newValue) => {
											setSelectedBqePhase(newValue);
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="BQE Phase"
												variant="outlined"
											/>
										)}
									/>
								</div>
							</div>
						</FormControl>
					)}

					{bqePhaseMode === "create" && phaseSelectionsComplete && (
						<FormControl sx={{ flex: 1 }}>
							{bqeParentProjectLoading && (
								<div className="FormRow">
									<CircularProgress size={20} />
								</div>
							)}
							{bqeParentProjectRecord && (
								<FormLabel>
									A new BQE phase will be created under "
									{bqeParentProjectRecord.displayName}" named "
									{phaseName.name}".
								</FormLabel>
							)}
						</FormControl>
					)}

					<Divider />

					<div className="dialogSubtitleWithActions">
						<span>CompanyCam Project</span>
						{!props.editing && (
							<ButtonGroup size="small">
								<Button
									sx={activeModeButtonSx(
										phaseSelectionsComplete && companyCamMode === "existing"
									)}
									variant={companyCamMode === "existing" ? "contained" : "outlined"}
									onClick={companyCamExistingClicked}
									disabled={companyCamMode === "existing" || !phaseSelectionsComplete}
								>
									Search Existing
								</Button>
								<Button
									sx={activeModeButtonSx(
										phaseSelectionsComplete && companyCamMode === "create"
									)}
									variant={companyCamMode === "create" ? "contained" : "outlined"}
									onClick={companyCamCreateClicked}
									disabled={companyCamMode === "create" || !phaseSelectionsComplete}
								>
									Create New
								</Button>
							</ButtonGroup>
						)}
					</div>

					{companyCamMode === "existing" && phaseSelectionsComplete && (
						<FormControl sx={{ flex: 1 }}>
							<div className="FormBlock">
								<div className="FormRow">
									<Autocomplete
										sx={{ flexGrow: 1 }}
										options={companyCamOptions}
										getOptionLabel={(option) =>
											option.name || option.id || ""
										}
										value={companyCamRecord}
										filterSelectedOptions
										onChange={companyCamProjectSelected}
										clearOnBlur={false}
										renderInput={(params) => (
											<TextField
												{...params}
												label="CompanyCam Project Name"
												placeholder="CompanyCam Project Name"
												value={companyCamSearchValue}
												onChange={(e) =>
													setCompanyCamSearchValue(e.target.value)
												}
												onKeyUp={companyCamSearchByNameChanged}
												InputProps={{
													...params.InputProps,
													endAdornment: (
														<>
															{companyCamSearchLoading && (
																<CircularProgress
																	color="inherit"
																	size={20}
																/>
															)}
														</>
													),
												}}
											/>
										)}
									/>
								</div>
							</div>
							{!companyCamRecord && companyCamID && (
								<FormHelperText>
									Currently linked to CompanyCam project ID:{" "}
									{companyCamID}
								</FormHelperText>
							)}
						</FormControl>
					)}

					{companyCamMode === "create" && phaseSelectionsComplete && (
						<FormControl sx={{ flex: 1 }}>
							<FormLabel>
								A new CompanyCam project will be created named "
								{buildCompanyCamProjectName()}".
							</FormLabel>
						</FormControl>
					)}

					<Divider />

					<div className="dialogSubtitleWithActions">
						<span>Dropbox Folder</span>
						{!props.editing && (
							<ButtonGroup size="small">
								<Button
									sx={activeModeButtonSx(
										phaseSelectionsComplete && dropboxMode === "existing"
									)}
									variant={dropboxMode === "existing" ? "contained" : "outlined"}
									onClick={dropboxExistingClicked}
									disabled={dropboxMode === "existing" || !phaseSelectionsComplete}
								>
									Search Existing
								</Button>
								<Button
									sx={activeModeButtonSx(
										phaseSelectionsComplete && dropboxMode === "create"
									)}
									variant={dropboxMode === "create" ? "contained" : "outlined"}
									onClick={dropboxCreateClicked}
									disabled={dropboxMode === "create" || !phaseSelectionsComplete}
								>
									Create New
								</Button>
							</ButtonGroup>
						)}
					</div>

					{dropboxMode === "existing" && phaseSelectionsComplete && (
						<FormControl sx={{ flex: 1 }}>
							<div className="FormBlock">
								<div className="FormRow">
									<DropboxFileSelector
										filePath={phaseDropBoxFolder}
										setFilePath={setPhaseDropBoxFolder}
									/>
								</div>
							</div>
						</FormControl>
					)}

					{dropboxMode === "create" && phaseSelectionsComplete && phaseProject?.dropbox_path && (
						<FormControl sx={{ flex: 1 }}>
							<FormLabel>
								A new phase folder, with its standard subfolders,
								will be created at:{" "}
								{`${phaseProject.dropbox_path}/${buildPhaseFolderName()}`.replace(
									/\/{2,}/g,
									"/"
								)}
							</FormLabel>
						</FormControl>
					)}

					<div className="editPhaseConsoleButtons">
						<Button
							variant="outlined"
							color="error"
							onClick={handleCancelClick}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleSubmitClick}
							disabled={loading}
						>
							Submit
						</Button>
					</div>
				</DialogContent>
			)}
		</Dialog>
	);
};

// EXPORT
export default PhaseConsoleEditAddDialog;
